import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { fetchCycleData, getVerifiedUserId } from "../lib/userDataService";

const hormones = [
  {
    id: "lh",
    title: "Luteinizing Hormone (LH)",
    content:
      "LH triggers ovulation. In PCOS, LH levels are sometimes persistently elevated, which may disrupt the normal ovulation cycle.",
  },
  {
    id: "fsh",
    title: "Follicle-Stimulating Hormone (FSH)",
    content:
      "FSH supports follicle development in the ovaries. An elevated LH-to-FSH ratio is sometimes discussed in PCOS-related hormone patterns.",
  },
  {
    id: "insulin",
    title: "Insulin & Blood Sugar",
    content:
      "Elevated fasting insulin may indicate insulin resistance, which is commonly associated with PCOS and can influence other hormone levels.",
  },
  {
    id: "testosterone",
    title: "Androgens & Testosterone",
    content:
      "Androgens including testosterone are present in all women. Elevated levels may be associated with PCOS symptoms such as acne, hair growth, and irregular cycles.",
  },
  {
    id: "amh",
    title: "Anti-Mullerian Hormone (AMH)",
    content:
      "AMH reflects ovarian reserve. Elevated levels may be associated with polycystic ovarian morphology, a common finding in PCOS evaluations.",
  },
];

function detectHormonePattern(h) {
  const flags = [];
  const ratio = h.lh && h.fsh ? h.lh / h.fsh : 0;
  if (ratio > 2) flags.push('elevated_lh_fsh');
  if (parseFloat(h.testosterone) > 70) flags.push('elevated_testosterone');
  if (parseFloat(h.insulin) > 12) flags.push('elevated_insulin');
  if (parseFloat(h.amh) > 3.5) flags.push('elevated_amh');

  if (flags.includes('elevated_lh_fsh') && flags.includes('elevated_testosterone'))
    return { pattern: 'Classic PCOS Hormonal Pattern', severity: 'high', flags };
  if (flags.includes('elevated_insulin') && flags.length >= 2)
    return { pattern: 'Insulin-Resistant Pattern', severity: 'moderate', flags };
  if (flags.length === 1)
    return { pattern: 'Mild Hormonal Variation', severity: 'low', flags };
  if (flags.length === 0)
    return { pattern: 'Within Common Reference Ranges', severity: 'none', flags };
  return { pattern: 'Mixed Hormonal Pattern', severity: 'moderate', flags };
}

const getMoodEnergyLabel = (val) => {
  if (val < 0.75) return 'Low';
  if (val < 1.5) return 'Moderate';
  if (val < 2.25) return 'Good';
  return 'High';
};

const getHormoneInsightText = (severity) => {
  switch (severity) {
    case 'high': return 'Your LH/FSH ratio and testosterone suggest a classic PCOS hormonal pattern. Consider discussing with your endocrinologist.';
    case 'moderate': return 'Some hormone markers are elevated. Regular monitoring and clinical follow-up are recommended.';
    case 'low': return 'A mild hormonal variation was detected. Worth mentioning at your next check-up.';
    default: return 'Your hormone markers appear within commonly cited ranges.';
  }
};

export default function InsightsScreen({ userId }) {
  const [expanded, setExpanded] = useState({});
  const [hormonePattern, setHormonePattern] = useState(null);
  const [hormoneData, setHormoneData] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [symptomTrends, setSymptomTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInsightsData = async () => {
      const uid = userId || (await getVerifiedUserId());
      if (!uid) { setLoading(false); return; }

      console.log('[InsightsScreen] Fetching data for user:', uid);

      // ── 1. Fetch cycle data from Supabase ──
      const cycle = await fetchCycleData(uid);
      if (cycle) setCycleData(cycle);
      console.log('[InsightsScreen] Insights fetched cycle:', cycle);

      // ── 2. Fetch latest hormone report ──
      const { data: latestHormone } = await supabase
        .from('hormone_reports')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestHormone) {
        const hData = {
          lh: latestHormone.lh,
          fsh: latestHormone.fsh,
          insulin: latestHormone.insulin,
          testosterone: latestHormone.testosterone || latestHormone.testosteone,
          amh: latestHormone.amh,
          ratio: latestHormone.lh && latestHormone.fsh
            ? (latestHormone.lh / latestHormone.fsh).toFixed(2)
            : null
        };
        setHormoneData(hData);
        setHormonePattern(detectHormonePattern(hData));
      }
      console.log('[InsightsScreen] Insights fetched hormones:', latestHormone);

      // ── 3. Fetch recent daily check-ins (last 7 days) ──
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { data: checkinRows } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', uid)
        .gte('checkin_date', weekAgo.toISOString().split('T')[0])
        .order('checkin_date', { ascending: true });

      if (checkinRows) setCheckins(checkinRows);

      // ── 4. Fetch symptom logs for trends (last 7 days) ──
      const { data: symptomRows } = await supabase
        .from('symptom_logs')
        .select('*')
        .eq('user_id', uid)
        .order('log_date', { ascending: false })
        .limit(7);

      if (symptomRows && symptomRows.length >= 2) {
        const sorted = [...symptomRows].sort((a, b) => a.log_date.localeCompare(b.log_date));
        const symptoms = ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'];
        const trends = [];

        symptoms.forEach(symptom => {
          const values = sorted.map(r => r[symptom] ?? 0);
          if (!values.some(v => v > 0)) return;

          const firstHalf = values.slice(0, Math.ceil(values.length / 2));
          const secondHalf = values.slice(Math.floor(values.length / 2));
          const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
          const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
          const diff = secondAvg - firstAvg;

          let trend = 'stable', icon = '\u2192', color = '#8B5CF6';
          if (diff > 0.5) { trend = 'increasing'; icon = '\u2191'; color = '#EF4444'; }
          else if (diff < -0.5) { trend = 'decreasing'; icon = '\u2193'; color = '#10B981'; }

          trends.push({
            symptom,
            label: symptom.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            trend, icon, color,
            currentValue: values[values.length - 1],
            avg: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)
          });
        });

        setSymptomTrends(trends);
      }

      setHasData(!!(cycle || latestHormone || (checkinRows && checkinRows.length > 0) || (symptomRows && symptomRows.length > 0)));
      setLoading(false);
      console.log('[InsightsScreen] Data loading complete');
    };

    loadInsightsData();
  }, [userId]);

  // Computed values
  const moodAvg = checkins.length > 0 ? checkins.reduce((s, c) => s + (c.mood || 0), 0) / checkins.length : 0;
  const energyAvg = checkins.length > 0 ? checkins.reduce((s, c) => s + (c.energy_level || c.energy || 0), 0) / checkins.length : 0;

  const getDaysSinceLastPeriod = () => {
    if (!cycleData?.lastPeriodDate) return null;
    return Math.floor((new Date() - new Date(cycleData.lastPeriodDate)) / 86400000);
  };

  const getNextPeriodDate = () => {
    if (!cycleData?.lastPeriodDate || !cycleData?.cycleLength) return null;
    const next = new Date(new Date(cycleData.lastPeriodDate).getTime() + cycleData.cycleLength * 86400000);
    return next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const daysSince = getDaysSinceLastPeriod();
  const isOverdue = cycleData?.cycleLength && daysSince > cycleData.cycleLength + 7;

  if (loading) {
    return (
      <div className="insights-screen" data-testid="insights-screen-loading" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 16
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '3px solid #EEEAF8', borderTopColor: '#6D5EF5',
          animation: 'spin 0.8s linear infinite'
        }} />
        <p style={{ color: '#8A8A9A', fontSize: 14 }}>Loading insights...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="insights-screen fade-in" data-testid="insights-screen">
      <h2>Insights</h2>

      {/* Card 1 - Weekly Summary Header */}
      <div style={{
        background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)', borderRadius: 20,
        padding: 24, marginBottom: 20, color: '#fff'
      }} data-testid="weekly-summary-card">
        <span style={{ fontSize: 10, textTransform: 'uppercase', opacity: 0.8, letterSpacing: '0.08em' }}>WEEKLY INSIGHTS</span>
        <h3 style={{ fontSize: 20, fontWeight: 700, marginTop: 8, marginBottom: 8, color: '#fff' }}>Your Week at a Glance</h3>
        <p style={{ fontSize: 14, opacity: 0.95, margin: 0 }}>
          {!hasData && 'Start logging to unlock personalized weekly insights.'}
          {hasData && checkins.length > 0 && !hormonePattern && `You have ${checkins.length} check-in${checkins.length > 1 ? 's' : ''} this week. Add a hormone report for deeper insights.`}
          {hasData && checkins.length > 0 && hormonePattern && `You have ${checkins.length} check-in${checkins.length > 1 ? 's' : ''} and hormone data this week.`}
          {hasData && checkins.length === 0 && hormonePattern && 'You have hormone data. Add daily check-ins to see mood and energy trends.'}
          {hasData && checkins.length === 0 && !hormonePattern && cycleData && 'Cycle data loaded. Add check-ins and hormones for richer insights.'}
        </p>
      </div>

      {/* Card 2 - Hormone Insight */}
      {hormonePattern && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginBottom: 16
        }} data-testid="hormone-insight-card">
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Hormone Pattern</h4>
          <span style={{
            background: hormonePattern.severity === 'high' ? '#FEE2E2' :
                        hormonePattern.severity === 'moderate' ? '#FEF3C7' : '#EDE9F8',
            color: hormonePattern.severity === 'high' ? '#991B1B' :
                   hormonePattern.severity === 'moderate' ? '#92400E' : '#6D5EF5',
            borderRadius: 20, padding: '6px 14px', fontSize: 13, display: 'inline-block', marginBottom: 10
          }}>
            {hormonePattern.pattern}
          </span>
          <p style={{ fontSize: 13, color: '#5A5A5A', lineHeight: 1.5, margin: 0 }}>
            {getHormoneInsightText(hormonePattern.severity)}
          </p>
          {/* Individual hormone flags */}
          {hormoneData && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              {hormoneData.ratio && parseFloat(hormoneData.ratio) > 2 && (
                <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 16, padding: '4px 10px', fontSize: 12 }}>
                  LH/FSH: {hormoneData.ratio}
                </span>
              )}
              {parseFloat(hormoneData.testosterone) > 70 && (
                <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 16, padding: '4px 10px', fontSize: 12 }}>
                  Testosterone: {hormoneData.testosterone}
                </span>
              )}
              {parseFloat(hormoneData.insulin) > 12 && (
                <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 16, padding: '4px 10px', fontSize: 12 }}>
                  Insulin: {hormoneData.insulin}
                </span>
              )}
              {parseFloat(hormoneData.amh) > 3.5 && (
                <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 16, padding: '4px 10px', fontSize: 12 }}>
                  AMH: {hormoneData.amh}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Weekly Symptom Trends */}
      {symptomTrends.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginBottom: 16
        }} data-testid="weekly-symptom-trends-card">
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Weekly Symptom Trends</h4>
          <p style={{ fontSize: 12, color: '#8A8A9A', marginBottom: 16 }}>Based on your last 7 days of logs</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {symptomTrends.map((item) => (
              <div key={item.symptom} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', background: '#F9FAFB', borderRadius: 12,
                borderLeft: `3px solid ${item.color}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: item.color, width: 24, textAlign: 'center' }}>
                    {item.icon}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#2F2F35' }}>{item.label}</span>
                </div>
                <span style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 12, fontWeight: 500, textTransform: 'capitalize',
                  background: item.trend === 'increasing' ? '#FEE2E2' : item.trend === 'decreasing' ? '#D1FAE5' : '#EDE9FE',
                  color: item.trend === 'increasing' ? '#991B1B' : item.trend === 'decreasing' ? '#065F46' : '#6D5EF5'
                }}>
                  {item.trend}
                </span>
              </div>
            ))}
          </div>

          {symptomTrends.some(t => t.trend === 'increasing') && (
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#FEF3C7', borderRadius: 10, fontSize: 13, color: '#92400E' }}>
              Some symptoms are trending upward. Consider logging daily to track patterns.
            </div>
          )}
          {symptomTrends.every(t => t.trend === 'stable' || t.trend === 'decreasing') && (
            <div style={{ marginTop: 14, padding: '12px 14px', background: '#D1FAE5', borderRadius: 10, fontSize: 13, color: '#065F46' }}>
              Your symptoms appear stable or improving this week.
            </div>
          )}
        </div>
      )}

      {/* Card 4 - Mood & Energy */}
      {checkins.length >= 3 && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginBottom: 16
        }} data-testid="mood-energy-card">
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Mood & Energy This Week</h4>
          {['Mood', 'Energy'].map((label, idx) => {
            const avg = idx === 0 ? moodAvg : energyAvg;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 13, width: 60 }}>{label}</span>
                <div style={{ flex: 1, height: 6, background: '#EDE9F8', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    width: `${(avg / 3) * 100}%`, height: '100%',
                    background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)', borderRadius: 3
                  }} />
                </div>
                <span style={{ fontSize: 12, color: '#8A8A9A', width: 60 }}>{getMoodEnergyLabel(avg)}</span>
              </div>
            );
          })}
          {moodAvg < 1.5 && (
            <p style={{ fontSize: 13, fontStyle: 'italic', color: '#8A8A9A', marginTop: 8, marginBottom: 0 }}>
              Mood has been lower this week.
            </p>
          )}
          {energyAvg < 1.5 && (
            <p style={{ fontSize: 13, fontStyle: 'italic', color: '#8A8A9A', marginTop: 4, marginBottom: 0 }}>
              Energy levels have been reduced this week.
            </p>
          )}
        </div>
      )}

      {/* Card 5 - Cycle Overview */}
      {cycleData && daysSince !== null && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginBottom: 16
        }} data-testid="cycle-overview-card">
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Cycle Overview</h4>
          <p style={{ fontSize: 13, color: '#5A5A5A', marginBottom: 6 }}>
            Days since last period: <strong>{daysSince}</strong>
          </p>
          {getNextPeriodDate() && (
            <p style={{ fontSize: 13, color: '#5A5A5A', marginBottom: 0 }}>
              Estimated next period: <strong>{getNextPeriodDate()}</strong>
            </p>
          )}
          {isOverdue && (
            <div style={{
              background: '#FEF3C7', borderRadius: 10, padding: '10px 14px',
              fontSize: 13, color: '#92400E', marginTop: 12
            }}>
              Your cycle may be running late.
            </div>
          )}
        </div>
      )}

      {/* Footer disclaimer */}
      <p style={{
        fontSize: 12, fontStyle: 'italic', color: '#8A8A9A', textAlign: 'center',
        marginTop: 24, marginBottom: 20, padding: '0 16px', lineHeight: 1.5
      }}>
        This tool provides educational insights and does not replace professional medical advice.
      </p>

      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Hormone Education</h3>

      {hormones.map((h) => (
        <div key={h.id} className="edu-accordion-card" data-testid={`edu-${h.id}`}>
          <div
            className="edu-accordion-header"
            onClick={() => setExpanded((prev) => ({ ...prev, [h.id]: !prev[h.id] }))}
          >
            <h4>{h.title}</h4>
            <span className={`edu-chevron${expanded[h.id] ? " open" : ""}`}>&#x203A;</span>
          </div>
          {expanded[h.id] && (
            <div className="edu-accordion-body">
              <p>{h.content}</p>
              <p className="edu-disclaimer">
                Reference ranges vary by laboratory. Clinical correlation is recommended.
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
